<template>
  <div v-if="visible" class="bmi-question">
    <h3 style="font-weight: bold;font-size: 27px;">
      Please respond to this 1 question so your order can be processed
    </h3>

    <form class="simple-form" style="margin: 10px 0; color: rgba(0, 0, 0, 0.85);" :id="formId" @submit.prevent="submitForm">
      <input type="hidden" name="consultation_id" :value="consultationId">
      <input type="hidden" name="highest_bmi" :value="calculatedBmi">

      <p>
        We see that you have marked a current weight that puts your current BMI under 30.0. Many states require that someone has had a maximum (historical weight) in their lifetime that gives a BMI over 30.0. Please provide your maximum weight that you have ever been.
      </p>

      <!-- Feet Input -->
      <p style="margin-bottom: 10px;">Please provide your Height (feet, inches)</p>
      <div class="form-group">
        <input
          type="number"
          name="feet"
          placeholder="Feet"
          class="form-control"
          required
          min="0"
          step="1"
          v-model.number="feetInput"
          @input="updateBmiDisplay"
          style="padding: 8px; border: 1px solid #ccc; border-radius: 4px; width: 30%; margin-right: 10px;"
        >

        <input
          type="number"
          name="inches"
          placeholder="Inches"
          class="form-control"
          required
          min="0"
          max="11"
          step="1"
          v-model.number="inchesInput"
          @input="updateBmiDisplay"
          style="padding: 8px; border: 1px solid #ccc; border-radius: 4px; width: 30%;"
        >
      </div>

      <p style="margin-bottom: 10px;">Please provide your HIGHEST weight that you have ever been</p>
      <!-- Weight Input -->
      <div class="form-group">
        <input
          type="text"
          name="highest_weight"
          :id="weightInputId"
          placeholder="Pounds"
          class="form-control"
          required
          v-model="weightInput"
          @input="updateBmiDisplay"
          style="padding: 8px; border: 1px solid #ccc; border-radius: 4px; width: 30%;"
        >
      </div>

      <p>Your Highest BMI is <span :style="{color: bmiColor, fontWeight: 'bold'}" :id="bmiOutputId">{{ calculatedBmi }}</span></p>

      <div class="questions__step--next-btn">
        <button type="submit" class="button button-primary" :disabled="isSubmitting">
          {{ isSubmitting ? 'Submitting...' : 'Submit Historical Highest BMI' }}
        </button>
      </div>
    </form>

    <div v-if="message" :class="['message', messageType]" :style="messageStyle">
      {{ message }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'HighestBmiQuestion',
  props: {
    consultationId: {
      type: [String, Number],
      required: true
    },
    feet: {
      type: Number,
      default: 0
    },
    inches: {
      type: Number,
      default: 0
    },
    isDashboard: {
      type: Boolean,
      default: false
    },
    formPrefix: {
      type: String,
      default: ''
    },
    csrfToken: {
      type: String,
      required: true
    },
  },
  data() {
    const prefix = this.formPrefix ? this.formPrefix + '_' : '';
    return {
      weightInput: '',
      feetInput: this.feet,
      inchesInput: this.inches,
      calculatedBmi: 0,
      isSubmitting: false,
      message: '',
      messageType: 'success',
      formId: prefix + 'highest-weight-form',
      weightInputId: prefix + 'highest_weight',
      bmiOutputId: prefix + 'highest_bmi_output',
      visible: true
    };
  },
  computed: {
    bmiColor() {
      return this.calculatedBmi < 30 ? 'red' : '#6BA53F';
    },
    messageStyle() {
      if (this.messageType === 'success') {
        return {
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '10px',
          borderRadius: '4px',
          margin: '10px 0',
          border: '1px solid #c3e6cb'
        };
      } else {
        return {
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '10px',
          borderRadius: '4px',
          margin: '10px 0',
          border: '1px solid #f5c6cb'
        };
      }
    }
  },
  methods: {
    calculateBmi(weight) {
      const weightNum = parseFloat(weight) || 0;
      const feetNum = parseInt(this.feetInput) || 0;
      const inchesNum = parseInt(this.inchesInput) || 0;

      const fullInches = feetNum * 12 + inchesNum;

      let bmiScore = 0;
      if (fullInches > 0 && weightNum > 0) {
        const bmiScoreFormula = (weightNum * 703) / (fullInches * fullInches);
        bmiScore = Math.round(bmiScoreFormula * 10) / 10;
      }

      return bmiScore;
    },
    updateBmiDisplay() {
      this.calculatedBmi = this.calculateBmi(this.weightInput);
    },
    async submitForm() {
      if (this.calculatedBmi < 30) {
        this.showMessage('Your Highest BMI should be more than 30', 'error');
        return;
      }
      this.isSubmitting = true;
      this.message = '';

      const formData = new FormData();
      formData.append('consultation_id', this.consultationId);
      formData.append('highest_weight', this.weightInput);
      formData.append('feet', this.feetInput);
      formData.append('inches', this.inchesInput);
      formData.append('highest_bmi', this.calculatedBmi);
      formData.append('_token', this.csrfToken);

      try {
        const response = await axios.post('/dashboard/update-highest-weight', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (response.data.success) {
          this.showMessage(response.data.message, 'success');
          setTimeout(() => {
            const card = this.$el.closest('.card');
            if (card) {
              card.style.display = 'none';
            }
            this.visible = false;
          }, 3000);
        } else {
          this.showMessage(response.data.message || 'An error occurred. Please try again.', 'error');
        }
      } catch (error) {
        console.error('Error:', error);
        let errorMessage = 'An error occurred. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        this.showMessage(errorMessage, 'error');
      } finally {
        this.isSubmitting = false;
      }
    },
    showMessage(text, type) {
      this.message = text;
      this.messageType = type;

      // Auto-hide message after 5 seconds
      setTimeout(() => {
        this.message = '';
      }, 5000);
    }
  }
};
</script>


// WEBPACK FOOTER //
// resources/assets/js/HighestBmiQuestion.vue